/**
 * Shared utilities for cleaning up authentication-related data
 */
import { logger } from "../../infrastructure/services/LoggerService";
import { clearSession } from "./sessionManager";
import { clearListBrowsingState } from "./listBrowsingStorage";
/**
 * sessionStorage key driving the OP-5119 org-less recovery state machine.
 * Present after a State-A discard so the next forced login adds `prompt=login`
 * and so a second org-less token escalates to State-B (logout + terminal page).
 */
export const RECOVERY_FLAG = "kc-org-recovery-attempted";
/**
 * Clear all Keycloak and authentication related data from storage
 */
export function cleanAuthData() {
    logger.debug("Cleaning authentication data...");
    clearSession();
    sessionStorage.removeItem("kc-token");
    sessionStorage.removeItem("kc-refresh-token");
    sessionStorage.removeItem("auth-redirect-path");
    sessionStorage.removeItem("stay-on-error-page");
}
/**
 * Remove the cached organization name from localStorage so that the next
 * Keycloak request goes out with the plain `organization` scope. Called
 * before any forced-login redirect (OP-5119) and on explicit logout.
 */
export function clearOrganizationCache() {
    localStorage.removeItem("organizationCache");
    logger.debug("Cleared organization cache");
}
/**
 * Clear the OP-5119 recovery flag so the next case-(b) detection starts as
 * State-A. Called on a successful auth-with-org-claim and on explicit logout.
 */
export function clearOrgRecoveryFlag() {
    sessionStorage.removeItem(RECOVERY_FLAG);
    logger.debug("Cleared org recovery flag");
}
/**
 * Whether a Keycloak parsed token (id_token or access_token) carries an
 * `organization` claim. Pure helper — pass `keycloak.idTokenParsed` from the
 * keycloak.ts plugin so this stays unit-testable without mocking keycloak-js.
 *
 * Parameter is `unknown` because `organization` is a Keycloak organization-
 * extension claim that isn't part of upstream `KeycloakTokenParsed` typings.
 */
export function hasOrganizationClaim(tokenParsed) {
    if (!tokenParsed || typeof tokenParsed !== "object") {
        return false;
    }
    return !!tokenParsed.organization;
}
/**
 * Mark logout time and clear auth data
 */
export function markLogoutAndCleanup() {
    logger.debug("Marking logout time and cleaning up...");
    sessionStorage.setItem("force-logout", Date.now().toString());
    cleanAuthData();
    clearOrganizationCache();
    clearOrgRecoveryFlag();
    clearListBrowsingState();
}
/**
 * Save user email for login_hint on reconnection
 */
export function saveLoginEmail(email) {
    if (email && email.trim()) {
        sessionStorage.setItem("last-login-email", email.trim());
        logger.debug(`Saved login email for reconnection: ${email}`);
    }
}
